// src/hooks/use-api.ts
import { 
  useQuery, 
  useMutation, 
  UseQueryOptions, 
  UseMutationOptions,
  useQueryClient
} from '@tanstack/react-query';
import axiosInstance from '../lib/axios-client';
import { toast } from './use-toast';

// Hook per query GET
export function useApiQuery<TData = unknown, TError = unknown>(
  queryKey: string[],
  url: string,
  options?: UseQueryOptions<TData, TError, TData>
) {
  return useQuery<TData, TError>({
    queryKey,
    queryFn: async () => {
      const response = await axiosInstance.get<TData>(url);
      return response.data;
    },
    ...options,
  });
}

// Hook per mutation POST
export function useApiMutation<TData = unknown, TVariables = unknown, TError = unknown>(
  url: string,
  options?: UseMutationOptions<TData, TError, TVariables>
) {
  const queryClient = useQueryClient();

  return useMutation<TData, TError, TVariables>({
    mutationFn: async (variables) => {
      const response = await axiosInstance.post<TData>(url, variables);
      return response.data;
    },
    onSuccess: (data, variables, context) => {
      toast({
        title: 'Operazione completata',
        description: 'I dati sono stati salvati con successo',
        status: 'success',
      });
      
      // Callback personalizzato se fornito
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    onError: (error: any, variables, context) => {
      toast({
        title: 'Errore',
        description: error.response?.data?.message || 'Si è verificato un errore',
        status: 'error',
      });
      
      // Callback personalizzato se fornito
      if (options?.onError) {
        options.onError(error, variables, context);
      }
    },
    ...options,
  });
}

// Hook per mutation PUT
export function useApiUpdate<TData = unknown, TVariables = unknown, TError = unknown>(
  url: string,
  options?: UseMutationOptions<TData, TError, TVariables>
) {
  const queryClient = useQueryClient();

  return useMutation<TData, TError, TVariables>({
    mutationFn: async (variables) => {
      const response = await axiosInstance.put<TData>(url, variables);
      return response.data;
    },
    onSuccess: (data, variables, context) => {
      toast({
        title: 'Aggiornamento completato',
        description: 'I dati sono stati aggiornati con successo',
        status: 'success',
      });
      
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    onError: (error: any, variables, context) => {
      toast({
        title: 'Errore di aggiornamento',
        description: error.response?.data?.message || 'Si è verificato un errore durante l\'aggiornamento',
        status: 'error',
      });
      
      if (options?.onError) {
        options.onError(error, variables, context);
      }
    },
    ...options,
  });
}

// Hook per mutation DELETE
export function useApiDelete<TData = unknown, TVariables = unknown, TError = unknown>(
  url: string,
  options?: UseMutationOptions<TData, TError, TVariables>
) {
  const queryClient = useQueryClient();

  return useMutation<TData, TError, TVariables>({
    mutationFn: async (variables) => {
      const response = await axiosInstance.delete<TData>(`${url}/${variables}`);
      return response.data;
    },
    onSuccess: (data, variables, context) => {
      toast({
        title: 'Eliminazione completata',
        description: 'L\'elemento è stato eliminato con successo',
        status: 'success',
      });
      
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    onError: (error: any, variables, context) => {
      toast({
        title: 'Errore di eliminazione',
        description: error.response?.data?.message || 'Si è verificato un errore durante l\'eliminazione',
        status: 'error',
      });
      
      if (options?.onError) {
        options.onError(error, variables, context);
      }
    },
    ...options,
  });
}